// Modules
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import React from 'react';
import _ from 'lodash';
import moment from 'moment';
import Promise from 'bluebird';

import { CheckboxInput } from 'components/forms/form-elements';
import { Page, PageHeader, PageContent, ActionSection } from 'components/page';
import {
  PrimaryButton,
  SecondaryButton
} from 'components/widgets/buttons/buttons';
import { SHORT_HUMAN_DATE_FORMAT } from 'constants';
import { TableCard } from 'components/content-containers/cards';
import { alerts } from 'modules/alerts/actions';
import { currency } from 'utils/format-utils';
import { didQueryUpdate } from 'utils/filter-utils';
import Billing from 'services/billing';
import CheckItems from 'utils/check-items';
import Company from 'services/data/company';
import CompanyLink from 'components/models/company/company-link';
import Filters from 'modules/filters/filters';
import Invoice from 'services/data/invoice';
import ExpenseBill from 'services/data/expense-bill';

import BillingTable from './components/billing-table';
import * as actions from './store/actions';


// Component
class BillingPage extends React.Component {
  static propTypes = {
    companies: PropTypes.oneOfType([
      PropTypes.array,
      PropTypes.object
    ]),
    anyItemsChecked: PropTypes.bool,
    fetching: PropTypes.bool,
    creatingInvoices: PropTypes.bool,
    errorCreatingInvoices: PropTypes.bool,
    query: PropTypes.object,
    total: PropTypes.number
  };

  constructor() {
    super();

    this.state = {};

    this.generateInvoices = this.generateInvoices.bind(this);
    this.checkReconciliation = this.checkReconciliation.bind(this);

    this.checkItems = new CheckItems(() => {
      actions.set({ companies: this.props.companies });
    });

    this.filterConfig = {
      basic: ['company'],
      advanced: [
        ['company', 'team', 'department'],
        ['releaseDate', 'billingType', 'billingStatus']
      ],
      defaults: {
        billingType: ['timeReport', 'billing', 'expenseBill'],
        department: ['Development', 'Technology'],
        releaseDate: { max: moment().toDate() },
        billingStatus: ['Not Billed']
      }
    };
  }

  componentWillReceiveProps(nextProps) {
    // If the query has updated search again
    if (didQueryUpdate(nextProps, this.props)) this.getData(nextProps.query);

    if (_.isEqual(nextProps.companies, this.props.companies)) return;

    const companies = _.cloneDeep(nextProps.companies);
    const billables = Billing.getBillables(companies);

    actions.set({ anyItemsChecked: _.filter(billables, 'checked').length > 0 });

    this.checkItems.setOnChange(() => actions.set({ companies }));
    this.checkItems.setList(billables);
  }

  componentWillUnmount() {
    actions.clearPageData();
  }

  /**
   * Gets all companies which have items which need to be invoiced
   */
  getData(passedQuery, options = {}) {
    if (!options.background) actions.set({ fetching: true });
    const query = passedQuery || this.props.query;
    const fullQuery = _.defaults(Billing.formatQuery(query), Billing.defaultQuery);
    const department = query && query.where && query.where.department;
    fullQuery.load = Billing.getDefaultLoad(query.where);
    let total = 0;
    delete fullQuery.limit;

    return Promise.all([
      Company.find(fullQuery),
      Company.getBillingEscalators()
    ])
      .spread((companies, billingEscalators) => {
        _.forEach(companies, (company) => {
          company.timeReports = _.sortBy(company.timeReports, timeReport => moment(timeReport.date).toDate());
          company.billings = _.sortBy(company.billings, billing => moment(billing.releaseDate).toDate());
          company.expenseBills = _.sortBy(company.expenseBills, bill => moment(bill.date).toDate());
          company.userEscalator = billingEscalators[company.id];

          if (department && department.length === 1) {
            company.timeReports = _.filter(company.timeReports, ['agreement.department', department[0]]);
            company.billings = _.filter(company.billings, ['agreement.department', department[0]]);
            company.expenseBills = _.filter(company.expenseBills, ['agreement.department', department[0]]);
          }

          _.forEach(company.expenseBills, (expenseBill) => {
            const requiresWorkOrder = _.get(expenseBill, 'agreement.requiresWorkOrder');
            const hasWorkOrder = _.get(expenseBill, 'ticket.workOrder');

            if (!expenseBill.approved) expenseBill.checkboxDisabled = 'This expense bill has not been approved yet';
            if (!expenseBill.reconciled) expenseBill.checkboxDisabled = 'This expense has not been reconciled';
            if (expenseBill.invoice) expenseBill.checkboxDisabled = 'This expense bill has already been invoiced';
            if (requiresWorkOrder && !hasWorkOrder) expenseBill.checkboxDisabled = 'Please set a work order on this ticket before billing';

            total += Number(expenseBill.amount);
          });

          _.forEach(company.billings, (billing) => {
            if (billing.invoice) billing.checkboxDisabled = 'This billing has already been invoiced';
            if (moment().isBefore(billing.releaseDate)) billing.checkboxDisabled = `This billing has not been released yet: ${moment(billing.releaseDate).format(SHORT_HUMAN_DATE_FORMAT)}`;

            total += Number(billing.amount);
          });

          _.forEach(company.timeReports, (report) => {
            const requiresWorkOrder = _.get(report, 'agreement.requiresWorkOrder');
            const hasWorkOrder = _.get(report, 'ticket.workOrder');

            if (requiresWorkOrder && !hasWorkOrder) report.checkboxDisabled = 'Please set a work order on this ticket before billing';
            if (report.invoice) report.checkboxDisabled = 'This time report has already been invoiced';

            total += Number(report.amount);
          });
        });

        _.remove(companies, (company) =>
          (!company.timeReports || !company.timeReports.length) &&
          (!company.billings || !company.billings.length) &&
          (!company.expenseBills || !company.expenseBills.length)
        );

        actions.set({ companies, total, fetching: false });
      })
      .catch(alerts.catchError);
  }

  /**
   * Hits the createMany endpoint and then refreshes the page data so items
   * which have already been invoiced disappear
   */
  generateInvoices() {
    actions.set({ creatingInvoices: true, errorCreatingInvoices: false });
    const invoices = Billing.createInvoices(this.props.companies);

    Invoice.createMany(invoices)
      .then(() =>
        this.getData(this.props.query, { background: true })
      )
      .then(() => {
        actions.set({ creatingInvoices: false, anyItemsChecked: false });
      })
      .catch((err) => {
        actions.set({ creatingInvoices: false, errorCreatingInvoices: true });
        alerts.catchError(err);
      });
  }

  checkReconciliation() {
    this.setState({ checkingReconciliation: true });

    return ExpenseBill.check()
      .then(() => {
        this.setState({ checkingReconciliation: false });
        alerts.success({ title: 'Reconciliation check complete!' });
        this.getData();
      });
  }

  /**
   * Creates a flat array of all of the billable items on every company which can
   * be used to toggle the checked state of a billable or set of billables. See
   * comments for getBillables() for more information on how this works.
   */
  render() {
    const {
      anyItemsChecked,
      companies,
      fetching,
      creatingInvoices,
      errorCreatingInvoices,
      query,
      total
    } = this.props;

    const { checkingReconciliation } = this.state;

    const refresh = () => this.getData(query, { background: true });

    const checked = _.filter(Billing.getBillables(companies), 'checked');
    const checkedTotal = _.reduce(checked, (sum, billable) => sum + Number(billable.amount), 0);

    const headerActions = (
      <div className="header-actions">
        <SecondaryButton onClick={this.checkReconciliation} showLoading={checkingReconciliation}>Check Reconciliation</SecondaryButton>
      </div>
    );

    return (
      <Page className="billing-list">
        <PageHeader title="Billing" actions={headerActions}>
          <ActionSection>
            <Filters className={anyItemsChecked ? 'hidden' : ''} config={this.filterConfig} />
            {anyItemsChecked &&
              <div className="checkbox-table-actions">
                <CheckboxInput input={{ value: anyItemsChecked || false, onChange: this.checkItems.toggleAllChecked }} />
                <PrimaryButton
                  onClick={this.generateInvoices}
                  disabled={!anyItemsChecked}
                  showLoading={creatingInvoices}
                  showComplete={!errorCreatingInvoices}
                  className="margin-right"
                >
                  <span>Generate Invoices</span>
                </PrimaryButton>
                <h2 className="total-value">Selected Total: {currency(checkedTotal)}</h2>
              </div>
            }
            {!fetching && <h2 className="total-value">Total: {currency(total)}</h2>}
          </ActionSection>
        </PageHeader>
        <PageContent
          fetching={fetching}
          emptyText="Nothing to invoice, adjust your filters!"
          emptyTextIcon="svg/filter.svg"
          showEmptyText={!fetching && _.isEmpty(companies)}
          className="billing bg-medium-blue-grey"
        >
          {_.map(companies, (company) =>
            <TableCard key={company.id} title={<CompanyLink company={company} className="unadorned" />}>
              <BillingTable company={company} toggleChecked={this.checkItems.toggleChecked} refreshData={refresh} />
            </TableCard>
          )}
        </PageContent>
      </Page>
    );
  }
}

// Exports
export default connect((state) => ({
  anyItemsChecked: state.billing.anyItemsChecked,
  companies: state.billing.companies,
  total: state.billing.total,
  fetching: state.billing.fetching,
  creatingInvoices: state.billing.creatingInvoices,
  errorCreatingInvoices: state.billing.errorCreatingInvoices,
  query: state.location.query
}))(BillingPage);
